import { Autocomplete, TextField, Typography, Box } from '@mui/material';
import ChevronDown from 'design-system/src/assets/icons/chevronDown.svg?react';
import styled from 'styled-components';
import { focusVisibleStylesNoWrapper } from 'design-system/src/sharedStyles';
import {
  dropdownDisabledStyling,
  dropdownErrorStyling,
  dropdownFieldsetStyling,
  dropdownSvgStyling,
  dropdownTextStyling,
} from './Dropdown.tsx.tsx';

type Option = {
  value: string;
  label: string;
  section: string;
};

export type DropdownTypeaheadWithSectionsProps = {
  value: string;
  onChange: (value: string | null) => void;
  options: Record<string, string[] | { value: string; label: string }[]>;
  placeholder?: string;
  disabled?: boolean;
  hasError?: boolean;
  className?: string;
  labelId?: string;
};

// Matches Dropdown.tsx styling
const StyledTextField = styled(TextField)<{ $hasError?: boolean }>`
  .MuiInputBase-root {
    display: flex;
    padding: 0;
    align-items: center;
    ${dropdownTextStyling}

    fieldset {
      ${dropdownFieldsetStyling}
    }

    &.Mui-focused fieldset {
      border: 1px solid var(--border-gray) !important;
      ${focusVisibleStylesNoWrapper}
    }

    &.Mui-disabled {
      ${dropdownDisabledStyling}
    }

    ${({ $hasError }) => $hasError && dropdownErrorStyling}
  }

  .MuiAutocomplete-endAdornment {
    right: 12px;

    svg {
      ${dropdownSvgStyling}
    }
  }

  .MuiAutocomplete-popupIndicatorOpen {
    transform: rotate(180deg);
  }
`;

export const DropdownTypeaheadWithSections = ({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  hasError,
  className,
  labelId,
}: DropdownTypeaheadWithSectionsProps) => {
  const flattenedOptions: Option[] = Object.entries(options).flatMap(([section, items]) =>
    items.map((item) => ({
      value: typeof item === 'string' ? item : item.value,
      label: typeof item === 'string' ? item : item.label,
      section,
    })),
  );

  const selectedOption = flattenedOptions.find((option) => option.value === value) || null;

  return (
    <Autocomplete
      value={selectedOption}
      onChange={(_, newValue) => {
        onChange(newValue?.value ?? null);
      }}
      disabled={disabled}
      options={flattenedOptions}
      groupBy={(option) => option.section}
      getOptionLabel={(option) => option.label}
      popupIcon={<ChevronDown />}
      sx={{
        '& .MuiOutlinedInput-root': {
          padding: '6px 12px !important',
        },
      }}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          placeholder={placeholder}
          $hasError={hasError}
          className={className}
          aria-labelledby={labelId}
        />
      )}
      renderGroup={(params) => (
        <Box key={params.key}>
          <Typography
            component="div"
            sx={{
              backgroundColor: 'var(--background-gray)',
              lineHeight: '1.5',
              padding: '8px 12px',
              fontWeight: 500,
            }}
          >
            {params.group}
          </Typography>
          {params.children}
        </Box>
      )}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          sx={{
            padding: '8px 12px',
            fontSize: '0.9375rem',
            lineHeight: '1.5',
          }}
        >
          {option.label}
        </Box>
      )}
      disablePortal
      fullWidth
    />
  );
};
